import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import { uploadAsset, deleteAsset } from '../lib/assetsApi';
    
    const { FiUpload, FiX, FiImage, FiTrash2, FiCheck, FiAlertCircle } = FiIcons;
    
    const AssetUploader = ({ type, assets, onChange, title, description }) => {
      const [uploading, setUploading] = useState(false);
      const [dragOver, setDragOver] = useState(false);
      const [uploadProgress, setUploadProgress] = useState({});
      const [error, setError] = useState(null);

      const handleFileSelect = async (files) => {
        if (files.length === 0) return;
        
        setUploading(true);
        setError(null);
        
        try {
          const uploadPromises = Array.from(files).map(async (file, index) => {
            const uploadId = `${Date.now()}_${index}`;
            
            // Show initial progress
            setUploadProgress(prev => ({
              ...prev,
              [uploadId]: { name: file.name, progress: 0, status: 'uploading' }
            }));

            try {
              const result = await uploadAsset(file, type);
              
              // Update progress to complete
              setUploadProgress(prev => ({
                ...prev,
                [uploadId]: { name: file.name, progress: 100, status: 'completed' }
              }));

              return {
                id: Math.random().toString(36).substr(2, 9),
                name: file.name,
                url: result.url,
                path: result.path,
                size: file.size,
                type: file.type,
                uploaded_at: new Date().toISOString()
              };
            } catch (err) {
              // Update progress to error
              setUploadProgress(prev => ({
                ...prev,
                [uploadId]: { name: file.name, progress: 0, status: 'error', error: err.message }
              }));
              throw err;
            }
          });

          const uploadedAssets = await Promise.all(uploadPromises);
          const newAssets = [...assets, ...uploadedAssets];
          onChange(newAssets);
          
          // Clear progress after a delay
          setTimeout(() => {
            setUploadProgress({});
          }, 3000);
          
        } catch (error) {
          console.error('Upload failed:', error);
          setError(error.message);
        } finally {
          setUploading(false);
        }
      };

      const handleFileInput = (event) => {
        handleFileSelect(event.target.files);
      };

      const handleDrop = (event) => {
        event.preventDefault();
        setDragOver(false);
        handleFileSelect(event.dataTransfer.files);
      };

      const handleDragOver = (event) => {
        event.preventDefault();
        setDragOver(true);
      };

      const handleDragLeave = (event) => {
        event.preventDefault();
        setDragOver(false);
      };

      const handleDelete = async (asset) => {
        if (!window.confirm(`Are you sure you want to delete ${asset.name}?`)) return;
        
        try {
          await deleteAsset(asset.path);
          const newAssets = assets.filter(a => a.id !== asset.id);
          onChange(newAssets);
        } catch (error) {
          console.error('Delete failed:', error);
          setError('Failed to delete asset. Please try again.');
        }
      };

      const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      };

      return (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-gray-600">{description}</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Upload Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Upload Area */}
          <div 
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Drop files here or click to browse
            </p>
            <p className="text-gray-600 mb-4">Supports JPG, PNG, SVG, PDF, DOCX files (Max 50MB)</p>
            <input 
              type="file" 
              multiple 
              accept="image/*,.pdf,.docx,.doc" 
              onChange={handleFileInput}
              className="hidden" 
              id={`${type}-upload`} 
              disabled={uploading}
            />
            <label 
              htmlFor={`${type}-upload`} 
              className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors cursor-pointer ${
                uploading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span>{uploading ? 'Uploading...' : 'Select Files'}</span>
            </label>
          </div>

          {/* Upload Progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="space-y-2">
              {Object.entries(uploadProgress).map(([id, progress]) => (
                <div key={id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">{progress.name}</span>
                    <span className="text-xs text-gray-600">
                      {progress.status === 'completed' ? 'Completed' : 
                       progress.status === 'error' ? 'Failed' : 'Uploading...'}
                    </span>
                  </div>
                  {progress.status === 'error' && (
                    <p className="text-xs text-red-600 mb-2">{progress.error}</p>
                  )}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        progress.status === 'completed' ? 'bg-green-600' :
                        progress.status === 'error' ? 'bg-red-600' : 'bg-blue-600'
                      }`}
                      style={{ width: `${progress.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Assets Grid */}
          {assets.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700">
                {assets.length} {assets.length === 1 ? 'file' : 'files'} uploaded
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {assets.map((asset, index) => (
                  <motion.div 
                    key={asset.id} 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="relative group bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden">
                      {asset.url ? (
                        <img 
                          src={asset.url} 
                          alt={asset.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full hidden items-center justify-center">
                        <SafeIcon icon={FiImage} className="w-8 h-8 text-gray-400" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {asset.name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {formatFileSize(asset.size)}
                      </p>
                    </div>
                    <button 
                      onClick={() => handleDelete(asset)}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };

    export default AssetUploader;